// @flow
import {ActionsObservable, ofType, StateObservable} from 'redux-observable';
import {
  endpoints,
  getNotificationsApi,
  seenAllNotificationsApi,
  seenNotificationApi,
} from './api';
import {map, switchMap} from 'rxjs/operators';
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';
import {GetSessionSuccess, LoginSuccess} from '../auth/duck';
import {buildRoute} from '../../http';
import {arrayToOrderedMap, orderedMapToArray} from '../../utils/array';

import type {RootState} from '../index';
import type {StoreNotification} from '../../entities/store-notification';
import type {Action} from '../../store/utils';
import type {Pagination} from '../../entities';
import {notificationAdapter} from './utils';

export type FetchNotificationsRequest = {
  reviewed?: number,
  size?: number,
  page?: number,
};

export type NotificationsAction = Action & {
  error?: string,
  notificationId?: string,
  fetchNotificationsRequest?: FetchNotificationsRequest,
  notifications?: StoreNotification[],
  notification?: StoreNotification,
};

export type NotificationsState = {
  isFetching?: boolean,
  isFetchingUnseen?: boolean,
  error?: string,
  unseenNotifications?: StoreNotification[],
  notifications?: StoreNotification[],
  pagination: Pagination,
};

export class FetchNotifications extends ActionCreator<NotificationsAction> {}
export class FetchNotificationsSuccess extends ActionCreator<NotificationsAction> {}
export class FetchNotificationsErrorFn extends ActionCreator<NotificationsAction> {}
export class FetchUnseenNotifications extends ActionCreator<NotificationsAction> {}
export class FetchUnseenNotificationsSuccess extends ActionCreator<NotificationsAction> {}
export class FetchUnseenNotificationsErrorFn extends ActionCreator<NotificationsAction> {}
export class SeenNotification extends ActionCreator<NotificationsAction> {}
export class SeenNotificationSuccess extends ActionCreator<NotificationsAction> {}
export class SeenNotificationErrorFn extends ActionCreator<NotificationsAction> {}
export class SeenAllNotifications extends ActionCreator<NotificationsAction> {}
export class SeenAllNotificationsSuccess extends ActionCreator<NotificationsAction> {}
export class SeenAllNotificationsErrorFn extends ActionCreator<NotificationsAction> {}
export class NotificationsResetState extends ActionCreator<NotificationsAction> {}

export class SetNotifications extends ActionCreator<NotificationsAction> {}

export const actions = {
  fetchNotifications: (payload: NotificationsAction) =>
    new FetchNotifications(payload),
  fetchUnseenNotifications: (payload: NotificationsAction) =>
    new FetchUnseenNotifications(payload),
  markNotificationAsSeen: (payload: NotificationsAction) =>
    new SeenNotification(payload),
  markAllNotificationsAsSeen: (payload: NotificationsAction) =>
    new SeenAllNotifications(payload),
  notificationsResetState: () => new NotificationsResetState(),

  addUnseenNotifications: (notifications: StoreNotification[]) =>
    new SetNotifications({
      notifications,
    }),
};

export const initialState = {
  pagination: {size: 10, page: 0, total: 0},
};

export default createReducer(initialState, {
  [FetchNotifications.type]: (state: NotificationsState) => ({
    ...state,
    isFetching: true,
    error: undefined,
  }),
  [FetchNotificationsSuccess.type]: (
    state: NotificationsState,
    action: NotificationsAction
  ) => {
    const newNotifications = arrayToOrderedMap(action.data.data, 'id');
    const notificationsArray = orderedMapToArray(newNotifications);

    return {
      ...state,
      notifications: notificationAdapter(notificationsArray),
      // notifications: [...(state.notifications || []), ...action.data.data],
      pagination: action.data.pagination,
      isFetching: undefined,
    };
  },
  [FetchNotificationsErrorFn.type]: (
    state: NotificationsState,
    action: NotificationsAction
  ) => ({
    ...state,
    isFetching: undefined,
    error: action.error,
  }),
  [FetchUnseenNotifications.type]: (state: NotificationsState) => ({
    ...state,
    isFetchingUnseen: true,
    error: undefined,
  }),
  [FetchUnseenNotificationsSuccess.type]: (
    state: NotificationsState,
    action: NotificationsAction
  ) => {
    const newUnseenNotifications = arrayToOrderedMap(action.data.data, 'id');
    const oldUnseenNotifications = arrayToOrderedMap(
      state.unseenNotifications,
      'id'
    );
    const unseenNotificationsMerged = orderedMapToArray(
      newUnseenNotifications.merge(oldUnseenNotifications)
    );

    //All fetch data is in real time and it's going to append at begin of the list
    return {
      ...state,
      unseenNotifications: notificationAdapter(unseenNotificationsMerged),
      isFetchingUnseen: undefined,
    };
  },
  [FetchUnseenNotificationsErrorFn.type]: (
    state: NotificationsState,
    action: NotificationsAction
  ) => ({
    ...state,
    isFetchingUnseen: undefined,
    error: action.error,
  }),
  [SeenNotification.type]: (state: NotificationsState) => ({...state}),
  [SeenNotificationSuccess.type]: (
    state: NotificationsState,
    action: NotificationsAction
  ) => {
    const unseenMap = arrayToOrderedMap(state.unseenNotifications, 'id');
    const idToDelete = action.notification ? action.notification.id : '';
    const newUnseenNotifications = orderedMapToArray(
      unseenMap.delete(idToDelete)
    );
    const aux = [...(state.notifications || [])];
    const index = aux.findIndex(n => n.id === action.notification.id);
    if (index > -1) {
      aux[index] = action.notification;
    }
    return {
      ...state,
      unseenNotifications: notificationAdapter(newUnseenNotifications),
      notifications: notificationAdapter(aux),
      error: undefined,
    };
  },
  [SeenNotificationErrorFn.type]: (
    state: NotificationsState,
    action: NotificationsAction
  ) => ({
    ...state,
    error: action.error,
  }),
  [SeenAllNotifications.type]: (state: NotificationsState) => ({...state}),
  [SeenAllNotificationsSuccess.type]: (state: NotificationsState) => {
    const aux = [...(state.notifications || [])];
    aux.map(n => {
      n.reviewed = true;
      return n;
    });
    return {
      ...state,
      notifications: notificationAdapter(aux),
      unseenNotifications: [],
    };
  },
  [SeenAllNotificationsErrorFn.type]: (
    state: NotificationsState,
    action: NotificationsAction
  ) => ({
    ...state,
    error: action.error,
  }),
  [NotificationsResetState.type]: () => initialState,
  [SetNotifications.type]: (
    state: NotificationsState,
    action: NotificationsAction
  ) => {
    const newUnseenNotifications = arrayToOrderedMap(
      action.notifications,
      'id'
    );
    const oldUnseenNotifications = arrayToOrderedMap(
      state.unseenNotifications,
      'id'
    );
    const unseenNotificationsMerged = orderedMapToArray(
      newUnseenNotifications.merge(oldUnseenNotifications)
    );

    //All fetch data is in real time and it's going to append at begin of the list
    return {
      ...state,
      unseenNotifications: notificationAdapter(unseenNotificationsMerged),
    };
  },
});

export function fetchUnseenNotificationsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(
      FetchUnseenNotifications.type,
      LoginSuccess.type,
      GetSessionSuccess.type
    ),
    switchMap(() =>
      getNotificationsApi({reviewed: '0'}).pipe(
        map(data => new FetchUnseenNotificationsSuccess({data})),
        buildCatchError(FetchUnseenNotificationsErrorFn)
      )
    )
  );
}

export function fetchNotificationsEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchNotifications.type),
    switchMap(({fetchNotificationsRequest}: NotificationsAction) => {
      const qf = fetchNotificationsRequest
        ? fetchNotificationsRequest
        : {
            page: state$.value.notifications.pagination.page,
            size: state$.value.notifications.pagination.size,
          };
      return getNotificationsApi(qf).pipe(
        map(data => new FetchNotificationsSuccess({data})),
        buildCatchError(FetchNotificationsErrorFn)
      );
    })
  );
}

export function seenNotificationEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SeenNotification.type),
    switchMap(({notificationId}: NotificationsAction) =>
      seenNotificationApi(undefined, {
        route: buildRoute(endpoints.SEEN_NOTIFICATION, [notificationId]),
      }).pipe(
        map(data => new SeenNotificationSuccess({notification: data})),
        buildCatchError(SeenNotificationErrorFn)
      )
    )
  );
}

export function seenAllNotificationsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SeenAllNotifications.type),
    switchMap(() =>
      seenAllNotificationsApi().pipe(
        map(data => new SeenAllNotificationsSuccess({data})),
        buildCatchError(SeenAllNotificationsErrorFn)
      )
    )
  );
}
